import React, { useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';

import { ariaAttributes } from '../../utils/accessibility';

interface AccordionProps {
  title: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
  titleClassName?: string;
  contentClassName?: string;
  id: string;
  showDetailButton?: boolean;
  onDetailClick?: () => void;
}

const Accordion: React.FC<AccordionProps> = ({
  title,
  children,
  defaultOpen = false,
  className = '',
  titleClassName = '',
  contentClassName = '',
  id,
  showDetailButton = false,
  onDetailClick,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const contentId = `accordion-content-${id}`;

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className={`border border-gray-200 rounded-lg mb-4 overflow-hidden ${className}`}>
      <div
        className={`flex justify-between items-center p-4 bg-white cursor-pointer ${titleClassName} ${isOpen ? 'bg-blue-50' : ''}`}
        onClick={toggleAccordion}
        {...ariaAttributes.accordion(isOpen, contentId)}
      >
        <div className="flex-grow font-medium">{title}</div>
        <div className="flex items-center">
          {showDetailButton && onDetailClick && (
            <motion.button
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                onDetailClick();
              }}
              className="mr-4 px-4 py-1.5 text-sm bg-gradient-to-r from-blue-600 to-primary text-white rounded-lg shadow-sm hover:shadow-md button-pulse"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
              type="button"
            >
              <span className="flex items-center">
                Ver Detalles
                <ArrowRight className="h-4 w-4 ml-1" />
              </span>
            </motion.button>
          )}
          {isOpen ? (
            <ChevronUp className="h-5 w-5 text-blue-600" />
          ) : (
            <ChevronDown className="h-5 w-5 text-blue-600" />
          )}
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            id={contentId}
            initial={{ height: 0, opacity: 0, scale: 0.98 }}
            animate={{ height: 'auto', opacity: 1, scale: 1 }}
            exit={{ height: 0, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
            className={`overflow-hidden ${contentClassName} optimize-animation`}
          >
            <motion.div
              className="p-4 bg-white border-t border-gray-200"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              {children}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Accordion;
